'use client';

import { useEffect } from 'react';
import { usePreservedCallback } from '@toss/react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import './events.web-zem-charge';
import { ZEM_CHARGE_POPUP_MESSAGE_TYPE, ZEM_CHARGE_POPUP_OPENER_ORIGIN_STORAGE_KEY, type ZemChargePopupMessage } from '@zep/common/routes';
import { useDialog } from '@zep/dialog';
import { trackEvent } from '@zep/logger/tracking';
import { buildZemChargeFailureProps, getZemChargeFailureEventName } from './zemChargeAnalytics';
import { getZemChargePopupContext, markZemChargeFailureTracked } from './zemChargePopupContext';
const getZemChargePopupTargetOrigin = () => {
  return window.sessionStorage.getItem(ZEM_CHARGE_POPUP_OPENER_ORIGIN_STORAGE_KEY) ?? window.location.origin;
};
const postZemChargeFailMessage = ({
  code,
  message,
  context
}: {
  code?: string | string[];
  message?: string | string[];
  context: ReturnType<typeof getZemChargePopupContext>;
}) => {
  if (!window.opener) {
    return;
  }
  const popupMessage: ZemChargePopupMessage = {
    type: ZEM_CHARGE_POPUP_MESSAGE_TYPE.FAIL,
    code: Array.isArray(code) ? code[0] : code,
    message: Array.isArray(message) ? message[0] : message,
    ...(context.place && {
      place: context.place
    }),
    chargeFlowId: context.chargeFlowId,
    ...(context.commerceFlowId && {
      commerceFlowId: context.commerceFlowId
    })
  };
  window.opener.postMessage(popupMessage, getZemChargePopupTargetOrigin());
};
export const ChargeZemFail = () => {
  const {
    query,
    push
  } = useRouter();
  const dialog = useDialog();
  const {
    t
  } = useTranslation(['common', 'manage'], {
    useSuspense: true
  });
  const {
    content,
    title
  } = query.code === 'PAY_PROCESS_CANCELED' || query.code === 'C0998' ? {
    title: t('manage:charge.fail.cancelTitle'),
    content: t('manage:charge.fail.cancelContent')
  } : {
    title: t('manage:charge.fail.failTitle'),
    content: `${t('manage:charge.fail.failReason', {
      message: query.message
    })}\n` + t('manage:charge.fail.failContent')
  };
  const showDialog = usePreservedCallback(() => {
    const code = Array.isArray(query.code) ? query.code[0] : query.code;
    const message = Array.isArray(query.message) ? query.message[0] : query.message;
    const chargeContext = getZemChargePopupContext();
    const failureEventName = getZemChargeFailureEventName(code);
    const failureEventProps = buildZemChargeFailureProps({
      code,
      context: chargeContext,
      message,
      stage: 'provider_callback'
    });
    if (markZemChargeFailureTracked(chargeContext.chargeFlowId, failureEventProps.code, failureEventName)) {
      if (failureEventName === 'web_zem_charge_cancel') {
        trackEvent('web_zem_charge_cancel', failureEventProps);
      } else {
        trackEvent('web_zem_charge_error', failureEventProps);
      }
    }
    postZemChargeFailMessage({
      code: query.code,
      message: query.message,
      context: chargeContext
    });
    const nextQuery = {
      ...query
    };
    if (nextQuery.message) delete nextQuery.message;
    if (nextQuery.code) delete nextQuery.code;
    dialog.alert({
      title,
      content
    }).then(() => {
      push({
        pathname: `/accounts/charge`,
        query: nextQuery
      });
    });
  });
  useEffect(() => {
    showDialog();
  }, [showDialog]);
  return <></>;
};